import { Popover as BasePopover } from '@base-ui/react/popover'
import {
  isValidElement,
  type ReactElement,
  type ReactNode,
  type RefObject,
  useState,
} from 'react'
import { overlayCopy } from '@/copy'
import { cx } from '../cx'
import { Icon } from '../Icon/Icon'
import { IconButton } from '../IconButton/IconButton'
import { useOverlayLayer } from '../overlay/escapeStack'
import { OverlayHeader, overlayClass } from '../overlay/Overlay'
import {
  type Placement,
  type SpaceStep,
  spaceOffset,
  toSideAlign,
} from '../overlay/positioning'
import styles from './Popover.module.css'

// Wraps Base UI Popover. Deviations: Escape via the page-wide stack;
// focus="anchor" turns off initial and final focus (Popover card, decisions:
// two focus modes); a press on a ref anchor is not an outside press (Base UI
// only knows a Trigger as its own). Tested.

type PopoverProps = {
  /** An element becomes the trigger; a ref anchors only, and its owner keeps the ARIA. */
  anchor: ReactElement | RefObject<HTMLElement | null>
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  placement?: Placement
  offset?: SpaceStep
  focus?: 'move' | 'anchor'
  title?: string
  /** The accessible name of an untitled popover. */
  label?: string
  children: ReactNode
}

export function Popover({
  anchor,
  open,
  defaultOpen,
  onOpenChange,
  placement = 'bottom-start',
  offset = 'space-4',
  focus = 'move',
  title,
  label,
  children,
}: PopoverProps) {
  const [inner, setInner] = useState(defaultOpen ?? false)
  const isOpen = open ?? inner
  const hasTrigger = isValidElement(anchor)
  const handle = useOverlayLayer(
    isOpen,
    (next, details: BasePopover.Root.ChangeEventDetails) => {
      if (
        !next &&
        !hasTrigger &&
        details.reason === 'outside-press' &&
        details.event.target instanceof Node &&
        anchor.current?.contains(details.event.target)
      ) {
        details.cancel()
        return
      }
      setInner(next)
      onOpenChange?.(next)
    },
  )
  const { side, align } = toSideAlign(placement)

  return (
    <BasePopover.Root open={isOpen} onOpenChange={handle} modal={false}>
      {hasTrigger && <BasePopover.Trigger render={anchor} />}
      <BasePopover.Portal>
        <BasePopover.Positioner
          side={side}
          align={align}
          sideOffset={spaceOffset(offset)}
          anchor={hasTrigger ? undefined : anchor}
          className={styles.positioner}
        >
          <BasePopover.Popup
            initialFocus={focus === 'anchor' ? false : undefined}
            finalFocus={focus === 'anchor' ? false : undefined}
            aria-label={title ? undefined : label}
            className={cx(overlayClass.surface, styles.popup)}
            data-weight="sm"
          >
            {title && (
              <OverlayHeader
                title={
                  <BasePopover.Title
                    // biome-ignore lint/a11y/useHeadingContent: Base UI Title supplies the children (the title text)
                    render={<h2 />}
                    className={overlayClass.title}
                  >
                    {title}
                  </BasePopover.Title>
                }
                close={
                  <BasePopover.Close
                    render={
                      <IconButton label={overlayCopy.close} ground="page">
                        <Icon name="cross-2" />
                      </IconButton>
                    }
                  />
                }
              />
            )}
            <div className={styles.body}>{children}</div>
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  )
}
